import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { ChartBarIcon } from '@heroicons/react/24/outline';

const HealthTrendChart = ({ dates = [], scores = [], currentScore = null, loading = false, days = 30 }) => {
  // Prepare data for Recharts
  const chartData = dates.map((date, index) => ({
    date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: scores[index],
    fullDate: date
  }));

  // Use all data since it's already filtered by the parent component based on selected days
  const filteredData = chartData;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-900">{data.fullDate}</p>
          <p className="text-sm text-gray-600 mt-1">
            Health Score: <span className="font-bold text-blue-600">{data.score}%</span>
          </p>
        </div>
      );
    }
    return null;
  };


  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 bg-gray-200 rounded w-40 animate-pulse"></div>
          <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-32 mb-6 animate-pulse"></div>

        {/* Chart Area Skeleton */}
        <div className="h-64 relative">
          {/* Simulated chart lines */}
          <div className="absolute inset-0 flex items-end gap-2 px-4 pb-8">
            {[...Array(12)].map((_, idx) => (
              <div
                key={idx}
                className="flex-1 bg-gray-200 animate-pulse"
                style={{
                  height: `${Math.random() * 60 + 20}%`,
                  animationDelay: `${idx * 100}ms`
                }}
              />
            ))}
          </div>
          {/* X axis skeleton */}
          <div className="absolute bottom-0 left-0 right-0 h-6 flex justify-between px-4">
            {[...Array(6)].map((_, idx) => (
              <div key={idx} className="h-3 bg-gray-200 rounded w-10 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!filteredData || filteredData.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <ChartBarIcon className="h-12 w-12 mb-2" />
          <p className="text-sm">No trend data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Health Score Trend</h3>
          <p className="text-sm text-gray-600 mt-1">Last {days} days • Track data quality over time</p>
        </div>

        <div className="p-2 rounded-lg bg-blue-100">
          <ChartBarIcon className="h-6 w-6 text-blue-600" />
        </div>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={filteredData}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
          >
            <defs>
              <linearGradient id="healthGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="date"
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="score"
              stroke="#3B82F6"
              strokeWidth={2}
              fill="url(#healthGradient)"
              dot={{ fill: '#3B82F6', r: 3 }}
              activeDot={{ r: 5, fill: '#2563EB' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
        <div>
          <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Current</p>
          <p className="text-2xl font-bold text-gray-900">
            {currentScore !== null ? Math.round(currentScore) : (filteredData[filteredData.length - 1]?.score || 0)}%
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Average</p>
          <p className="text-2xl font-bold text-gray-900">
            {Math.round(filteredData.reduce((sum, d) => sum + d.score, 0) / filteredData.length) || 0}%
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Peak</p>
          <p className="text-2xl font-bold text-gray-900">
            {Math.max(...filteredData.map(d => d.score)) || 0}%
          </p>
        </div>
      </div>
    </div>
  );
};

export default HealthTrendChart;
